import { useCallback, useEffect, useRef, useState } from 'react';
import { copyToClipboard } from './clipboard';

/**
 * A copy button's feedback (TOADO-974): 'yes' only when the clipboard took the
 * text, 'failed' when it refused, cleared after `showMs`. Only the latest copy
 * speaks: a later press restarts the clock rather than being cut short by an
 * earlier one's, and nothing is set after the button has gone.
 */
export function useCopyFeedback(showMs = 1400) {
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
  const attempt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => {
    attempt.current += 1;
    clearTimeout(timer.current);
  }, []);
  const copy = useCallback(async (text: string) => {
    const mine = ++attempt.current;
    clearTimeout(timer.current);
    const ok = await copyToClipboard(text);
    if (attempt.current !== mine) return;
    setCopied(ok ? 'yes' : 'failed');
    timer.current = setTimeout(() => setCopied(null), showMs);
  }, [showMs]);
  return [copied, copy] as const;
}
